/**
 * src/auth/AuthContext.tsx
 *
 * Authentication context — Microsoft Entra ID only (MSAL popup / PKCE).
 */

import {
  createContext, useContext, useState, useEffect, useCallback,
  type ReactNode,
} from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { graphScopes } from "./msalConfig";
import {
  getMsal, setSessionToken, getCachedSessionUser, exchangeForSessionToken,
  getSessionTokenExpiresAt, registerSilentReauth, registerSessionInvalidNotifier,
  registerReconnectingNotifier, trySilentReauthQuiet, type SessionUser,
} from "./msalClient";
import { type UserRole, DEFAULT_ROLE } from "./roles";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id:        string;
  name:      string;
  email:     string;
  avatar?:   string;
  provider?: "microsoft";
}

interface AuthContextValue {
  user:              AuthUser | null;
  /** Rôle effectif (override de session ou rôle réel). */
  role:              UserRole;
  /** Rôle réel venant du serveur (inchangé par les overrides). */
  baseRole:          UserRole;
  /** Passe temporairement à un autre rôle pour la session (null = réinitialiser). */
  overrideRole:      (r: UserRole | null) => void;
  isAuthenticated:   boolean;
  /** true uniquement pendant la restauration de session au chargement de l'app (mount). */
  isLoading:         boolean;
  /** true pendant le flow loginMicrosoft() déclenché par un clic sur LoginPage — distinct de
   *  isLoading pour que App.tsx puisse réserver le message "Connexion…" à ce cas précis. */
  isAuthenticating:  boolean;
  error:             string | null;
  loginMicrosoft:    () => Promise<void>;
  logout:            () => Promise<void>;
  getMicrosoftToken:  () => Promise<string | null>;
  clearError:         () => void;
  /**
   * Timestamp (ms) d'expiration du **token de session réel** (~1h, cf. docs/authentification.md) —
   * pas le compteur client "12h" (purement local, sert seulement à décider si l'app tente de
   * restaurer une session au chargement). C'est cette valeur qui pilote le bandeau d'avertissement
   * (App.tsx) : elle reflète le moment où les appels API commenceront réellement à échouer en 401.
   */
  sessionExpiresAt:  number | null;
  /**
   * Réauthentifie en place (ssoSilent puis repli loginPopup, comme loginMicrosoft) et renouvelle le
   * token de session — sans jamais passer isLoading à true, pour ne pas démonter l'écran en cours
   * (App.tsx affiche LoadingScreen plein écran tant que isLoading est vrai, ce qui perdrait l'état
   * React d'un formulaire/rapport en cours d'édition). Réservé aux appels déclenchés par un geste
   * utilisateur (le repli loginPopup ouvre une popup, bloquée par le navigateur hors d'un clic).
   * Retourne true si la reconnexion a réussi.
   */
  reauthenticate:    () => Promise<boolean>;
  /**
   * true si un rafraîchissement silencieux de session a échoué en tâche de fond (déclenché par un
   * 401 sur un appel API quelconque, cf. authFetch.ts) — plus aucune requête protégée n'aboutira
   * sans reconnexion interactive. Effacé par tout succès de reauthenticate()/loginMicrosoft().
   */
  sessionInvalid:    boolean;
  /**
   * true pendant qu'un rafraîchissement silencieux **réactif** (déclenché par un 401, cf.
   * authFetch.ts::trySilentReauth()) est en cours — jamais pour le rafraîchissement périodique
   * proactif (10 min), qui reste invisible tant qu'il réussit. Sert à afficher un <LoadingScreen/>
   * pendant la pause où la requête d'origine attend ce rafraîchissement (cf. App.tsx).
   */
  reconnectingSilently: boolean;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Session helpers ───────────────────────────────────────────────────────────

const SESSION_KEY = "report_app_user";

/**
 * Intervalle du rafraîchissement silencieux périodique (cf. useEffect dans AuthProvider). Réduit la
 * fenêtre de propagation d'un changement de rôle en base (ex. rétrogradation d'un utilisateur) à
 * cette durée au lieu d'attendre la pleine expiration du token (~1h) — sans réintroduire de lookup
 * DB par requête : resolveRoleFromDb() (server/lib/auth.ts) tourne déjà à chaque émission de token
 * (login ou refresh), ce timer se contente d'en émettre plus souvent. Ne garantit pas une révocation
 * immédiate (cf. docs/authentification.md, "Points en suspens") : un client qui n'exécute jamais ce
 * timer (hostile, ou ssoSilent indisponible dans son environnement) continue d'utiliser son token
 * jusqu'à sa vraie expiration — même plafond qu'avant, pas de régression.
 */
const SILENT_ROLE_REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

interface SessionData { user: AuthUser; }

/**
 * Persiste l'utilisateur (id/name/email/provider) pour survivre à un rechargement de page — pas de
 * notion d'expiration ici : c'est le token de session réel (~1h, cf. getSessionTokenExpiresAt()) qui
 * gouverne exclusivement la validité de la session restaurée (cf. loadSession() ci-dessous).
 */
function saveSession(user: AuthUser): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user }));
}

function clearSession() { sessionStorage.removeItem(SESSION_KEY); }

/**
 * Purge les brouillons locaux de rapports appartenant à cet utilisateur — clé
 * `report-draft:{userId}:{siteId}:{periodKey}` (cf. ReportA4.tsx, préfixe dupliqué ici faute de
 * module de constantes partagé entre auth et report-engine ; changer l'un implique l'autre). Appelé
 * au logout() : ces brouillons peuvent contenir des données de rapport non sauvegardées, à ne pas
 * laisser traîner indéfiniment dans localStorage sur un poste partagé après déconnexion.
 */
function clearLocalDrafts(userId: string) {
  if (!userId) return;
  const prefix = `report-draft:${userId}:`;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) localStorage.removeItem(key);
    }
  } catch { /* stockage indisponible */ }
}

/**
 * Flow MSAL partagé par loginMicrosoft()/reauthenticate() (allowPopup=true) et
 * reauthenticateSilent() (allowPopup=false, cf. plus bas) : ssoSilent puis, si autorisé, repli
 * loginPopup ; échange de l'ID token contre le token de session interne. Ne touche à aucun state
 * React — appelant responsable de saveSession()/setUser()/etc.
 */
async function performMicrosoftLogin(allowPopup: boolean): Promise<{ user: AuthUser; sessionUser: SessionUser }> {
  const msal = getMsal();
  await msal.initialize();
  let result;
  try { result = await msal.ssoSilent(graphScopes); }
  catch (e) {
    if (!allowPopup) throw e;
    result = await msal.loginPopup(graphScopes);
  }
  const { token: sessionToken, user: sessionUser } = await exchangeForSessionToken(result.idToken);
  setSessionToken(sessionToken);
  const u: AuthUser = {
    id:       result.account.homeAccountId,
    name:     sessionUser.name,
    email:    sessionUser.email,
    provider: "microsoft",
  };
  return { user: u, sessionUser };
}

/**
 * Utilisateur persisté (id/name/email/provider), sans vérification d'expiration : le mount
 * useEffect de AuthProvider ne restaure la session que si getCachedSessionUser() (token réel,
 * exp vérifiée) réussit aussi — c'est ce dernier qui tranche, jamais un âge arbitraire ici.
 */
function loadSession(): AuthUser | null {
  try {
    const r = sessionStorage.getItem(SESSION_KEY);
    if (!r) return null;
    const { user } = JSON.parse(r) as SessionData;
    return user;
  } catch { return null; }
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,         setUser]         = useState<AuthUser | null>(null);
  const [baseRole,          setBaseRole]          = useState<UserRole>(DEFAULT_ROLE);
  const [roleOverride,      setRoleOverride]      = useState<UserRole | null>(null);
  const role = roleOverride ?? baseRole;
  const [sessionExpiresAt,  setSessionExpiresAt]  = useState<number | null>(() => getSessionTokenExpiresAt());
  const [sessionInvalid,    setSessionInvalid]    = useState(false);
  const [reconnectingSilently, setReconnectingSilently] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => {
    // loadSession() restaure {id,name,email,provider} sans notion d'expiration propre (cf. sa
    // docstring) ; getCachedSessionUser() est ce qui vérifie réellement que le token (≤1h) est
    // encore valide et porte le rôle. Si le token a expiré, le rôle ne peut pas être restauré sans
    // un nouveau token — on repart d'un état déconnecté plutôt que de garder un utilisateur affiché
    // sans rôle fiable (cf. CLAUDE.md, section Authentification).
    const saved = loadSession();
    const sessionUser = getCachedSessionUser();
    if (saved && sessionUser) {
      setUser(saved);
      setBaseRole(sessionUser.role);
      setSessionExpiresAt(getSessionTokenExpiresAt());
    } else {
      clearSession(); setSessionToken(null); setUser(null); setSessionExpiresAt(null);
    }
    setIsLoading(false);
  }, []);

  const loginMicrosoft = useCallback(async () => {
    setIsAuthenticating(true); setError(null);
    try {
      // performMicrosoftLogin(true) : silent SSO first to avoid popup flash when an active session
      // exists, repli loginPopup sinon (déclenché par un clic — popup autorisée). Échange l'ID token
      // MSAL contre le token de session interne (email/name/role déjà résolus et vérifiés côté
      // serveur) — cf. msalClient.ts. Ce même appel journalise la connexion et met à jour user_roles
      // côté serveur, plus besoin d'un fetch /auth/login séparé.
      const { user: u, sessionUser } = await performMicrosoftLogin(true);
      saveSession(u);
      setUser(u); setRoleOverride(null); setSessionExpiresAt(getSessionTokenExpiresAt());
      setBaseRole(sessionUser.role); setSessionInvalid(false);
    } catch (e: unknown) {
      const msg = (e as Error).message ?? "";
      if (!msg.includes("user_cancelled")) setError("Connexion Microsoft échouée : " + msg);
    } finally { setIsAuthenticating(false); }
  }, []);

  // Réauthentification interactive pour un écran déjà ouvert (ex. sauvegarde qui échoue en 401 car
  // le token de session ~1h a expiré alors que la session cliente 12h est encore affichée, ou clic
  // sur "Prolonger la session") : même flow MSAL que loginMicrosoft (popup autorisée — toujours
  // déclenché par un geste utilisateur), mais isLoading n'est jamais touché pour ne pas démonter
  // l'écran courant (cf. docstring AuthContextValue.reauthenticate).
  const reauthenticate = useCallback(async (): Promise<boolean> => {
    try {
      const { user: u, sessionUser } = await performMicrosoftLogin(true);
      saveSession(u);
      setUser(u); setRoleOverride(null); setSessionExpiresAt(getSessionTokenExpiresAt());
      setBaseRole(sessionUser.role); setSessionInvalid(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Réauthentification silencieuse (ssoSilent uniquement, jamais de popup) — enregistrée auprès de
  // authFetch.ts via registerSilentReauth() (cf. useEffect plus bas) pour réagir à un 401 déclenché
  // hors de tout geste utilisateur (une requête API arrivant après expiration du token pendant que
  // l'utilisateur lit une page sans interagir).
  const reauthenticateSilent = useCallback(async (): Promise<boolean> => {
    try {
      const { user: u, sessionUser } = await performMicrosoftLogin(false);
      saveSession(u);
      setUser(u); setRoleOverride(null); setSessionExpiresAt(getSessionTokenExpiresAt());
      setBaseRole(sessionUser.role); setSessionInvalid(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Pont vers authFetch.ts (hors arbre React, cf. msalClient.ts) — enregistré/désenregistré avec le
  // cycle de vie du provider.
  useEffect(() => {
    registerSilentReauth(reauthenticateSilent);
    registerSessionInvalidNotifier(() => setSessionInvalid(true));
    registerReconnectingNotifier(setReconnectingSilently);
    return () => {
      registerSilentReauth(null);
      registerSessionInvalidNotifier(null);
      registerReconnectingNotifier(null);
    };
  }, [reauthenticateSilent]);

  // Rafraîchissement silencieux périodique tant qu'un utilisateur est connecté (cf. docstring de
  // SILENT_ROLE_REFRESH_INTERVAL_MS) — passe par trySilentReauthQuiet() (msalClient.ts), pas
  // directement par reauthenticateSilent(), pour partager le même verrou de déduplication que le
  // retry réactif sur 401 (authFetch.ts::trySilentReauth()) : si les deux tombent en même temps, un
  // seul ssoSilent part réellement, jamais deux appels /auth/login concurrents. "Quiet" : n'affiche
  // jamais le <LoadingScreen/> pour ce déclenchement proactif (cf. msalClient.ts). Best-effort : un
  // échec (ex. ssoSilent indisponible dans cet environnement) est simplement ignoré, le token en
  // cours reste utilisé jusqu'à sa vraie expiration, aucune dégradation par rapport au comportement
  // précédent.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => { trySilentReauthQuiet(); }, SILENT_ROLE_REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user]);

  const logout = useCallback(async () => {
    try {
      const msal = getMsal();
      await msal.initialize();
      // logoutPopup() triggers a COOP error since login.microsoftonline.com now
      // enforces Cross-Origin-Opener-Policy: same-origin on its pages, preventing
      // window.closed polling. clearCache() clears local MSAL tokens without popup.
      await msal.clearCache();
    } catch { /* ignore */ }
    if (user) clearLocalDrafts(user.id);
    setSessionToken(null);
    clearSession(); setUser(null); setBaseRole(DEFAULT_ROLE); setRoleOverride(null);
    setSessionExpiresAt(null); setSessionInvalid(false);
  }, [user]);

  const getMicrosoftToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      const msal    = getMsal();
      await msal.initialize();
      const account = msal.getAllAccounts()[0];
      const result  = await msal.acquireTokenSilent({ ...graphScopes, account });
      return result.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const result = await getMsal().acquireTokenPopup(graphScopes);
        return result.accessToken;
      }
      return null;
    }
  }, [user]);

  const clearError = useCallback(() => setError(null), []);

  const overrideRole = useCallback((r: UserRole | null) => setRoleOverride(r), []);

  return (
    <AuthContext.Provider value={{
      user, role, baseRole, overrideRole,
      isAuthenticated: !!user, isLoading, isAuthenticating, error,
      loginMicrosoft, logout, getMicrosoftToken, clearError,
      sessionExpiresAt, reauthenticate, sessionInvalid, reconnectingSilently,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used inside <AuthProvider>");
  return ctx;
}
